import React, { useState, useEffect } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Container,
  ButtonGroup,
} from "reactstrap";
import { Link } from "react-router-dom";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CreditLogService from "../../../services/OrderingServices/CreditLogService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import CountUp from "react-countup";
import toast from "react-hot-toast";
import TimeRangeFilter from "../../../Components/Common/TimeRangeFilter";

const CreditLogSummary = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(5); // default All

  // const dateRangeEnum = {
  //   OneDay: 1,
  //   OneWeek: 2,
  //   OneMonth: 3,
  //   ThreeMonths: 4,
  //   All: 5,
  // };

  const timeRanges = [
    { label: "All", value: 5, color: "primary" },
    { label: "1 Day", value: 1, color: "info" },
    { label: "7 Days", value: 2, color: "info" },
    { label: "1 Month", value: 3, color: "warning" },
    { label: "1 Year", value: 4, color: "danger" },
  ];

  const fetchCreditLogs = async (period) => {
    setLoading(true);
    try {
      const response = await CreditLogService.getSummary(period); // send integer period
      setData(response);
    } catch (error) {
      console.error("Error fetching credit logs:", error);
      toast.error("Failed to fetch credit logs");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
    fetchCreditLogs(period);
  };

  useEffect(() => {
    fetchCreditLogs(selectedPeriod);
  }, []);

  // Summary Card Component
  const totalBalanced = data
    .filter((t) => t.totalRemaining > 0)
    .reduce((sum, t) => sum + t.totalRemaining, 0);

  const SummaryCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm border-0 mb-3">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div className="w-100">
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <div className="d-flex justify-content-between w-100">
            <h4 className="fs-2 fw-bold" style={{ color }}>
              <CountUp start={0} end={value} duration={0} separator="," />
            </h4>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: color + "20",
                color,
                width: "36px",
                height: "36px",
              }}
            >
              <i className={`${icon} fs-5`} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  // DataTable Columns
  const columns = [
    { name: "S.N", selector: (_, i) => i + 1, width: "60px" },
    {
      name: "Customer Name",
      selector: (row) => row.customerName,
      cell: (row) => (
        <Link to={`/credit-logs/customer/${row.customerId}`}>
          {row.customerName}
        </Link>
      ),
    },
    {
      name: "Balanced Amt",
      selector: (row) => row.totalRemaining,
      cell: (row) => (
        <div className="text-end">
          {row.totalRemaining > 0 ? (
            <NumberWithCommas number={row.totalRemaining} />
          ) : (
            "-"
          )}
        </div>
      ),
      right: true,
      width: "150px",
    },
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Credit Summary" pageTitle="Reports" />

        <Row className="mt-2">
          <Col lg={6} md={6} sm={12}>
            <SummaryCard
              title="Total Balanced Amount"
              value={totalBalanced}
              color="#28a745"
              icon="bx bx-receipt"
            />
          </Col>
        </Row>

        {/* DataTable */}
        <div
          className="p-3 p-lg-4 mt-3"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          {" "}
          <div className="mb-3 d-flex justify-content-between align-items-center">
            {/* Left: Title */}
            <h5 className="mb-1 fw-bold text-dark">Credit Log Summary</h5>

            {/* Right: Payment type dropdown and filter button */}
            <Col lg={6} className="text-md-end">
              <TimeRangeFilter
                value={selectedPeriod}
                onChange={(period) => {
                  setSelectedPeriod(period);
                  fetchCreditLogs(period);
                }}
              />
            </Col>
          </div>{" "}
          <hr style={{ borderColor: "#000000ff", marginTop: "0rem" }} />
          <CustomDataTable
            columns={columns}
            data={data}
            noHeader
            pagination={data.length > 0}
            striped
            highlightOnHover
            responsive
            persistTableHead
            noDataComponent={
              <div className="text-center my-5 w-100">
                <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                <h5 className="fw-semibold text-secondary">
                  No data Available
                </h5>
                <p className="text-muted mb-0">
                  Please select a period to view credit logs.
                </p>
              </div>
            }
          />
        </div>
      </div>
    </Container>
  );
};

export default CreditLogSummary;
